import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetCatalogQuery, useGetRecordQuery, useMeQuery } from '@platform/state/api';
import { useLabel } from '@platform/hooks/useLabel';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { routes } from '@platform/core/lib/routes';
import type { ICrumb } from './ICrumb';
import { crumbsOf } from './crumbsOf';
import { catalogLabel, navGroup, placement } from './navigation';
import { recordTypeTitle } from './recordTypeTitle';

/**
 * Системні сторінки робочого простору поза модулем → ключ підпису (перший сегмент шляху, ADR-028; порожній — Головна): «Моя робота» —
 * рівень «Головна», бізнес-аналітика, учасники й запрошення — «Учасники» (як у прототипі v4). Профіль крихт не має (прототип v4).
 */
const PAGE_LABELS: Record<string, string> = {
  '': 'nav.home', inbox: 'nav.inbox', search: 'nav.search', modules: 'nav.modules', recent: 'homeNav.recent', favourites: 'homeNav.favourites',
  work: 'homeNav.label', bi: 'biPage.crumb', members: 'membersPage.crumb',
};

/** Назва напряму (групи верхнього меню) для крихт огляду напряму. */
const areaLabel = (key: string) => { const g = navGroup(key); return g ? catalogLabel(g) : null; };

/**
 * Крихти поточної сторінки за маршрутом (layout/breadcrumbs): назва робочого простору (GET /me), група й підгрупа модуля з моделі
 * навігації (placement), назви модуля й дочірньої таблиці з каталогу, запис — з кешу RTK Query (той самий запит, що й у сторінки запису).
 * Для сторінок модуля шапку з крихтами рендерить PageFrame; для системних сторінок — оболонка (WorkspaceLayout); системні модулі
 * «Адміністрування» й «Налаштування» — «Простір › Адміністрування (› Розділ)» (учасники й запрошення — без розділу, теги — «Простір ›
 * Теги › Налаштування»), конструктор модулів — «Простір › Конструктор модулів ›
 * Модуль › Налаштування» (як у прототипі). `recordLabel` — підпис запису замість «КОД · Назва» (сторінка
 * запису: «Завдання 101», як у прототипі v4); `pageLabel` — підпис розділу системного модуля замість типового (рівень «Глобально»:
 * «Каталог модулів» замість «Модулі», прототип SYS_T['cust-mods']).
 */
export function usePageCrumbs(recordLabel?: string | null, pageLabel?: string | null): ICrumb[] {
  const { t } = useTranslation();
  const label = useLabel();
  const { pathname } = useLocation();
  const { ws, module, rt, id } = useWorkspaceParams();
  const { data: me } = useMeQuery();
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws });
  const { data: record } = useGetRecordQuery({ ws, rt, id }, { skip: !ws || !rt || !id });
  const workspaceName = me?.workspaces.find((w) => w.id === ws)?.name ?? '';
  if (!module) {
    const [segment = '', sub, item] = pathname.split('/').filter(Boolean);
    const area = areaLabel(decodeURIComponent(segment));
    if (area) return crumbsOf({ workspaceName, page: label(area) || null, label });
    if (segment === 'customize' && sub === 'builder') {
      // прототип v4: «Простір › Конструктор модулів › Модуль › Налаштування» (усі, крім простору, — без посилань)
      const title = recordTypeTitle(catalog, item && decodeURIComponent(item), label);
      return [{ label: workspaceName, to: routes.home() }, { label: t('sysPages.crumbs.builder') }, ...(title ? [{ label: title }] : []), { label: t('sysPages.customize') }];
    }
    if (segment === 'customize' && sub === 'tags') {
      // прототип v4: крихта «Теги» стоїть перед системним модулем — «Простір › Теги › Налаштування»
      return [{ label: workspaceName, to: routes.home() }, { label: t('sysPages.crumbs.tags') }, { label: t('sysPages.customize') }];
    }
    if (segment === 'admin' || segment === 'customize') {
      const system = { label: t(`sysPages.${segment}`), to: `/${segment}` };
      // учасники й запрошення в режимі адміністрування — без власної крихти (прототип: PT.members лише поза системним модулем)
      const own = sub && !(segment === 'admin' && (sub === 'members' || sub === 'invitations'));
      return crumbsOf({ workspaceName, system, page: own ? pageLabel || t(`sysPages.crumbs.${sub}`, { defaultValue: sub }) : null, label });
    }
    const pageKey = PAGE_LABELS[segment];
    return crumbsOf({ workspaceName, page: pageKey ? t(pageKey) : null, label });
  }
  const summary = catalog?.modules.find((m) => m.key === module);
  const main = summary?.recordTypes.find((r) => r.main)?.key;
  const child = rt && rt !== main ? summary?.recordTypes.find((r) => r.key === rt) : undefined;
  const under = (base: string) => pathname === base || pathname.startsWith(`${base}/`);
  const section = under(routes.moduleSettings(module)) ? t('menu.settings') : under(routes.moduleDesign(module)) ? t('menu.design') : null;
  return crumbsOf({
    workspaceName, label, section,
    module: { key: module, label: summary ? label(summary.name) : module },
    placement: placement(module),
    table: child ? { key: child.key, label: label(child.name) } : null,
    record: id ? recordLabel || (record ? (record.title ? `${record.display} · ${record.title}` : record.display) : null) : null,
  });
}
